import { blockRemoteContent } from "./SandboxedMailBody";
import { sanitizeComposeHtml } from "./composeSanitize";
import { trenneAntwortPraefix } from "../../utils/betreffPraefix";

/**
 * Zitat, Empfänger und Betreff für „Antworten"/„Allen antworten"/„Weiterleiten"
 * — typneutral, damit Postfach-Mails (`MailDetailWindow`) und abgelegte
 * Mail-Dateien (`.eml`/`.msg`, T-247) DENSELBEN Zitat-Sanitizer und denselben
 * `data-kmvs-quote`-Marker benutzen. Zwei Fassungen liefen auseinander.
 */

/** „13.10.2026, 09:42" — leer bei ungültigem Datum. */
export const formatiereZitatDatum = (d: Date | null | undefined): string => {
    if (!(d instanceof Date) || isNaN(d.getTime())) return "";
    const dd = d.getDate().toString().padStart(2, "0");
    const mm = (d.getMonth() + 1).toString().padStart(2, "0");
    const y = d.getFullYear();
    const h = d.getHours().toString().padStart(2, "0");
    const min = d.getMinutes().toString().padStart(2, "0");
    return `${dd}.${mm}.${y}, ${h}:${min}`;
};

const escapeHtml = (s: string): string =>
    s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

/**
 * Outlook-artiges Zitat für den Text der Antwort.
 *
 * KONSISTENZ P0-C: Das Original wandert in den `contentEditable`-Editor und
 * geht beim Senden als `innerHTML` wieder hinaus — deshalb durch DIESELBEN
 * Helfer wie Lese- und Sendepfad:
 *   1. `blockRemoteContent(…, { block: true })` → Remote-Quellen neutralisieren
 *      (kein Zählpixel im Zitat).
 *   2. `sanitizeComposeHtml(…)` → `<script>`/`on*`/`<iframe>` entfernen.
 * `blob:`-Adressen (aufgelöste Inline-Bilder einer Archivdatei) gibt es beim
 * Empfänger nicht; sie fliegen vorher heraus.
 *
 * `data-kmvs-quote` markiert den zitierten Verlauf für den Sendepfad (SP2): ohne
 * ihn machte die `data:`→`cid`-Umwandlung beim Senden den ganzen Verlauf zu
 * Anhängen der eigenen Antwort. Der Backend-Sanitizer entfernt den Marker.
 *
 * `absenderName` darf HTML-Text sein (Bestandsverhalten des Postfachs); eine
 * Mail-Datei übergibt ihn bereits maskiert über `kopfMaskieren`.
 */
export function baueAntwortZitat(eingabe: {
    datum: Date | null | undefined;
    absenderName: string;
    absenderAdresse?: string | null;
    html: string;
    kopfMaskieren?: boolean;
}): string {
    const name = eingabe.kopfMaskieren ? escapeHtml(eingabe.absenderName) : eingabe.absenderName;
    const adresse = eingabe.absenderAdresse
        ? ` &lt;${eingabe.kopfMaskieren ? escapeHtml(eingabe.absenderAdresse) : eingabe.absenderAdresse}&gt;`
        : "";
    const header = `Am ${formatiereZitatDatum(eingabe.datum)} schrieb ${name}${adresse}:`;
    const original = (eingabe.html || "").replace(/\s(?:src|srcset|href)\s*=\s*(["'])blob:[^"']*\1/gi, "");
    const raw = `<br/><br/><div data-kmvs-quote="1" style="border-left:2px solid #ccc;padding-left:12px;color:#555;">${header}<br/><br/>${original}</div>`;
    const remoteStripped = blockRemoteContent(raw, { block: true }).html;
    return sanitizeComposeHtml(remoteStripped);
}

/**
 * Empfänger für „Allen antworten" (Outlook-Standard):
 *   - `to` = Original-Absender (leer-gefiltert).
 *   - `cc` = Original-An + Original-Cc, ohne Rücksicht auf Groß-/Klein-
 *     schreibung dedupliziert, OHNE die eigenen Adressen und OHNE den Absender.
 *   - Bcc wird nie übernommen.
 */
export function baueAllenAntwortenEmpfaenger(
    mail: {
        von: string | null | undefined;
        an?: Array<{ address?: string | null } | null | undefined> | null;
        cc?: Array<{ address?: string | null } | null | undefined> | null;
    },
    eigeneAdressen: Array<string | null | undefined>,
): { to: string[]; cc: string[] } {
    const von = (mail.von || "").trim();
    const senderLower = von.toLowerCase();
    const eigene = new Set(eigeneAdressen.map((a) => (a || "").trim().toLowerCase()).filter(Boolean));
    const to = von ? [von] : [];
    const seen = new Set<string>();
    const cc: string[] = [];
    for (const addr of [...(mail.an ?? []), ...(mail.cc ?? [])]) {
        const a = (addr?.address || "").trim();
        if (!a) continue;
        const lower = a.toLowerCase();
        if (eigene.has(lower)) continue;
        if (lower === senderLower) continue;
        if (seen.has(lower)) continue;
        seen.add(lower);
        cc.push(a);
    }
    return { to, cc };
}

/**
 * Betreff mit Antwort-/Weiterleitungsmarke („AW: …", „WG: …" wie in
 * Outlook-DE). Eine vorhandene Marke wird ersetzt, nicht gestapelt — kein
 * „AW: AW: …".
 */
export function antwortBetreff(praefix: "AW" | "WG", betreff: string | null | undefined): string {
    const rest = trenneAntwortPraefix(betreff).rest;
    return `${praefix}: ${rest}`.trim();
}
